/**
 * Styles for the rebuilt bulk send flow. Values come from
 * `BulkSend.tokens.ts`, never from a literal.
 *
 * Objects here stay plain (never pre-cast to `SxProps`) so call sites can
 * compose them; anything conditional is exported as a function returning one
 * composed literal, which keeps `sx` from hitting TS2769.
 */

import { Theme } from "@mui/material";
import tokens from "./BulkSend.tokens";
import { signer } from "theme/colors";

const t = tokens;
const p = tokens.palette;

/**
 * Below `md` the flow is the phone layout (the app's `md` is 900px). Written
 * as a raw query rather than a theme callback so these stay plain objects a
 * call site can compose — and never spread two objects that both carry this
 * key, or one silently wipes the other.
 */
const PHONE = "@media (max-width:899.95px)";

/** Every step's frame: the wizard chrome is shared, the body is per step. */
export const bulkShellStyles = {
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: p.white,
  },

  header: {
    height: t.header.height,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: t.header.gap,
    px: t.header.paddingX,
    borderBottom: `1px solid ${t.header.borderColor}`,
    backgroundColor: p.white,
    boxSizing: "border-box",
  },

  badge: {
    display: "flex",
    alignItems: "center",
    gap: t.header.pillGap,
    px: t.header.pillPaddingX,
    py: t.header.pillPaddingY,
    borderRadius: t.header.pillRadius,
    backgroundColor: t.header.pillBg,
    border: `1px solid ${t.header.pillBorder}`,
    fontSize: t.header.pillFontSize,
    fontWeight: t.header.pillFontWeight,
    // The accent is 4.06:1 on the pill fill; text takes the dark shade (1.4.3).
    color: signer.orange.dark,
    letterSpacing: t.header.pillLetterSpacing,
    whiteSpace: "nowrap",
    flexShrink: 0,
    boxSizing: "border-box",
  },

  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: t.header.gap,
    flexShrink: 0,
  },

  body: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
  },

  /**
   * A step's centred column. Each step passes the frame its counterpart uses
   * in the normal create-request flow (see `page` in the tokens), so the two
   * wizards never disagree about how wide the content is.
   */
  page: {
    pt: t.page.paddingTop,
    px: t.page.paddingX,
    pb: t.page.paddingBottom,
    boxSizing: "border-box",
    [PHONE]: {
      pt: t.mobile.bodyPaddingTop,
      px: t.mobile.gutter,
      pb: "24px",
    },
  },

  stepPage: (variant: "document" | "list" | "review") => ({
    width: "100%",
    maxWidth:
      variant === "document" ? t.page.documentMaxWidth : t.page.listMaxWidth,
    mx: "auto",
    pt:
      variant === "document"
        ? t.page.documentPaddingTop
        : variant === "review"
          ? t.page.reviewPaddingTop
          : t.page.listPaddingTop,
    px: t.page.paddingX,
    pb: t.page.paddingBottom,
    boxSizing: "border-box",
    [PHONE]: {
      maxWidth: "none",
      pt: t.mobile.bodyPaddingTop,
      px: t.mobile.gutter,
      pb: "24px",
    },
  }),

  pageTitle: {
    m: 0,
    mb: t.page.titleGap,
    [PHONE]: { fontSize: t.mobile.titleFontSize },
    fontSize: t.page.titleFontSize,
    fontWeight: t.page.titleFontWeight,
    letterSpacing: t.page.titleLetterSpacing,
    color: p.ink,
  },

  pageSub: {
    m: 0,
    mb: t.page.subMarginBottom,
    fontSize: t.page.subFontSize,
    color: t.page.subColor,
  },

  /** Panel opened by a step's pills — same column, no second top padding. */
  stepPanel: (variant: "document" | "list") => ({
    width: "100%",
    maxWidth:
      variant === "document" ? t.page.documentMaxWidth : t.page.listMaxWidth,
    mx: "auto",
    px: t.page.paddingX,
    pb: t.page.paddingBottom,
    boxSizing: "border-box",
    [PHONE]: { maxWidth: "none", px: t.mobile.gutter, pb: "24px" },
  }),
};

/** The panel shape shared by the template picker and saved lists. */
export const templatePickerStyles = {
  panel: {
    width: "100%",
    maxWidth: t.templatePicker.width,
    border: `1px solid ${p.borderLight}`,
    borderRadius: t.templatePicker.radius,
    backgroundColor: p.white,
    overflow: "hidden",
    boxSizing: "border-box",
  },

  header: {
    display: "flex",
    alignItems: "center",
    gap: t.templatePicker.headerGap,
    p: t.templatePicker.headerPadding,
    borderBottom: `1px solid ${p.borderLight}`,
  },

  headerTitle: {
    flex: 1,
    fontSize: t.templatePicker.headerFontSize,
    fontWeight: t.templatePicker.headerFontWeight,
    color: p.ink,
  },

  notice: {
    display: "flex",
    alignItems: "center",
    gap: t.templatePicker.noticeGap,
    p: t.templatePicker.noticePadding,
    borderBottom: `1px solid ${p.borderSoft}`,
    backgroundColor: t.templatePicker.noticeBg,
    fontSize: t.templatePicker.noticeFontSize,
    color: p.body,
    lineHeight: t.templatePicker.noticeLineHeight,
  },

  noticeTick: {
    width: t.templatePicker.noticeTickSize,
    height: t.templatePicker.noticeTickSize,
    borderRadius: "50%",
    backgroundColor: p.success,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  list: {
    p: t.templatePicker.listPadding,
    display: "flex",
    flexDirection: "column",
    gap: t.templatePicker.listGap,
  },

  card: (selected: boolean) => ({
    border: `${t.templatePicker.cardBorderWidth} solid ${
      selected ? p.accent : p.borderLight
    }`,
    backgroundColor: selected ? p.accentSoft : p.white,
    borderRadius: t.templatePicker.cardRadius,
    p: t.templatePicker.cardPadding,
    display: "flex",
    flexDirection: "column",
    gap: t.templatePicker.cardGap,
    cursor: "pointer",
    textAlign: "left",
    width: "100%",
    boxSizing: "border-box",
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "2px",
    },
  }),

  cardRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    width: "100%",
    minWidth: 0,
  },

  thumb: {
    width: t.templatePicker.thumbWidth,
    height: t.templatePicker.thumbHeight,
    borderRadius: t.templatePicker.thumbRadius,
    border: `1px solid ${p.borderLight}`,
    backgroundColor: p.white,
    flexShrink: 0,
  },

  cardName: {
    display: "block",
    fontSize: t.templatePicker.nameFontSize,
    fontWeight: 700,
    color: p.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  cardMeta: {
    display: "block",
    fontSize: t.templatePicker.metaFontSize,
    color: p.muted,
    mt: t.templatePicker.metaMarginTop,
  },

  radio: (selected: boolean) => ({
    width: t.templatePicker.radioSize,
    height: t.templatePicker.radioSize,
    borderRadius: "50%",
    border: `1.5px solid ${selected ? p.accent : p.borderStrong}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxSizing: "border-box",
    "&::after": {
      content: '""',
      width: t.templatePicker.radioDotSize,
      height: t.templatePicker.radioDotSize,
      borderRadius: "50%",
      backgroundColor: selected ? p.accent : "transparent",
    },
  }),

  roles: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    pt: t.templatePicker.rolesPaddingTop,
    borderTop: `1px solid ${p.borderSoft}`,
    width: "100%",
  },

  rolesLabel: {
    fontSize: t.templatePicker.rolesLabelFontSize,
    fontWeight: 800,
    letterSpacing: t.templatePicker.rolesLabelLetterSpacing,
    textTransform: "uppercase",
    color: p.muted,
  },

  roleRow: {
    display: "flex",
    alignItems: "center",
    gap: t.templatePicker.roleGap,
    width: "100%",
  },

  roleAvatar: (color: string) => ({
    width: t.templatePicker.roleAvatarSize,
    height: t.templatePicker.roleAvatarSize,
    borderRadius: "50%",
    backgroundColor: color,
    color: p.white,
    fontSize: t.templatePicker.roleAvatarFontSize,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),

  roleName: {
    fontSize: t.templatePicker.roleNameFontSize,
    fontWeight: 700,
    color: p.ink,
  },

  roleFields: {
    fontSize: t.templatePicker.roleFieldsFontSize,
    color: p.muted,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  roleOrder: {
    ml: "auto",
    fontSize: t.templatePicker.roleOrderFontSize,
    fontWeight: 700,
    color: p.label,
    backgroundColor: p.pill,
    borderRadius: "999px",
    p: t.templatePicker.roleOrderPadding,
    whiteSpace: "nowrap",
    flexShrink: 0,
  },

  footer: {
    p: t.templatePicker.footerPadding,
  },

  emptyWrap: {
    p: t.templatePicker.listPadding,
    pb: t.templatePicker.footerPadding,
  },
};

/** Step 2, door B — the editor's roles rail. */
export const rolesRailStyles = {
  root: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },

  header: {
    p: t.rolesRail.headerPadding,
    display: "flex",
    flexDirection: "column",
    [PHONE]: { p: "4px 0 0" },
  },

  eyebrow: {
    fontSize: t.rolesRail.eyebrowFontSize,
    fontWeight: 700,
    letterSpacing: t.rolesRail.eyebrowLetterSpacing,
    textTransform: "uppercase",
    color: p.muted,
  },

  hint: {
    fontSize: t.rolesRail.hintFontSize,
    color: p.muted,
    lineHeight: t.rolesRail.hintLineHeight,
    mt: t.rolesRail.hintMarginTop,
  },

  list: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    p: t.rolesRail.listPadding,
    display: "flex",
    flexDirection: "column",
    gap: t.rolesRail.listGap,
    [PHONE]: {
      flexDirection: "row",
      overflowX: "auto",
      overflowY: "hidden",
      p: "4px 0 8px",
      gap: "7px",
    },
  },

  card: (active: boolean) => ({
    [PHONE]: { flex: "0 0 auto", minWidth: 208 },
    border: `${t.rolesRail.cardBorderWidth} solid ${
      active ? p.accentBorder : p.borderLight
    }`,
    backgroundColor: active ? p.accentSoft : p.white,
    borderRadius: t.rolesRail.cardRadius,
    p: t.rolesRail.cardPadding,
    display: "flex",
    flexDirection: "column",
    gap: t.rolesRail.cardGap,
    cursor: "pointer",
    boxSizing: "border-box",
  }),

  cardRow: {
    display: "flex",
    alignItems: "center",
    gap: "9px",
  },

  badge: (color: string) => ({
    width: t.rolesRail.badgeSize,
    height: t.rolesRail.badgeSize,
    borderRadius: t.rolesRail.badgeRadius,
    backgroundColor: color,
    color: p.white,
    fontSize: t.rolesRail.badgeFontSize,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),

  input: (active: boolean) => ({
    flex: 1,
    minWidth: 0,
    height: t.rolesRail.inputHeight,
    border: `1px solid ${active ? p.accentBorder : p.borderLight}`,
    borderRadius: t.rolesRail.inputRadius,
    backgroundColor: p.white,
    fontFamily: "inherit",
    fontSize: t.rolesRail.inputFontSize,
    fontWeight: 700,
    color: p.ink,
    px: t.rolesRail.inputPaddingX,
    boxSizing: "border-box",
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "1px",
    },
  }),

  remove: {
    width: t.rolesRail.removeSize,
    height: t.rolesRail.removeSize,
    borderRadius: t.rolesRail.badgeRadius,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: p.muted,
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    flexShrink: 0,
    "&:hover": { color: p.dangerText },
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "1px",
    },
  },

  meta: {
    display: "flex",
    alignItems: "center",
    gap: t.rolesRail.metaGap,
  },

  orderPill: {
    fontSize: t.rolesRail.orderPillFontSize,
    fontWeight: 700,
    color: p.label,
    backgroundColor: p.pill,
    borderRadius: "999px",
    p: t.rolesRail.orderPillPadding,
    whiteSpace: "nowrap",
  },

  /** A role with no fields can still sign, but has nothing to fill in. */
  fieldCount: (isEmpty: boolean) => ({
    fontSize: t.rolesRail.fieldCountFontSize,
    fontWeight: 600,
    color: isEmpty ? p.dangerText : p.body,
  }),

  arrows: {
    ml: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "1px",
  },

  arrow: (disabled: boolean) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    backgroundColor: "transparent",
    p: 0,
    color: disabled ? p.borderMain : p.muted,
    cursor: disabled ? "default" : "pointer",
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "1px",
    },
  }),

  add: {
    [PHONE]: { flex: "0 0 auto", px: "14px" },
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: t.rolesRail.addGap,
    height: t.rolesRail.addHeight,
    border: `1.5px dashed ${p.borderMain}`,
    borderRadius: t.rolesRail.addRadius,
    backgroundColor: "transparent",
    color: p.body,
    fontFamily: "inherit",
    fontSize: t.rolesRail.addFontSize,
    fontWeight: 700,
    cursor: "pointer",
    flexShrink: 0,
    "&:hover": { borderColor: p.accent, color: p.accent },
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "2px",
    },
  },
};

/** Step 3 — which role varies per recipient, which is pinned for the batch. */
export const rolesStyles = {
  rows: {
    display: "flex",
    flexDirection: "column",
    gap: t.roles.rowsGap,
  },

  row: (varies: boolean) => ({
    border: `${t.roles.rowBorderWidth} solid ${
      varies ? p.accentBorder : p.borderLight
    }`,
    backgroundColor: varies ? p.accentSoft : p.white,
    borderRadius: t.roles.rowRadius,
    overflow: "hidden",
    boxSizing: "border-box",
  }),

  rowInner: {
    display: "flex",
    alignItems: "center",
    gap: t.roles.rowGap,
    p: t.roles.rowPadding,
    flexWrap: "wrap",
    [PHONE]: {
      alignItems: "flex-start",
      gap: "12px",
      p: "14px",
    },
  },

  accentBar: (color: string) => ({
    width: t.roles.accentBarWidth,
    alignSelf: "stretch",
    borderRadius: t.roles.accentBarRadius,
    backgroundColor: color,
    flexShrink: 0,
    minHeight: t.roles.accentBarMinHeight,
  }),

  avatar: (color: string) => ({
    width: t.roles.avatarSize,
    height: t.roles.avatarSize,
    borderRadius: "50%",
    backgroundColor: color,
    color: p.white,
    fontSize: t.roles.avatarFontSize,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),

  identity: {
    flex: `0 0 ${t.roles.nameWidth}px`,
    minWidth: 0,
    [PHONE]: { flex: "1 1 auto" },
  },

  roleName: {
    display: "block",
    fontSize: t.roles.nameFontSize,
    fontWeight: t.roles.nameFontWeight,
    color: p.ink,
  },

  fieldNote: {
    display: "block",
    fontSize: t.roles.fieldNoteFontSize,
    color: p.muted,
    mt: t.roles.fieldNoteMarginTop,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  toggle: {
    [PHONE]: { width: "100%" },
    display: "flex",
    backgroundColor: t.roles.toggleBg,
    borderRadius: t.roles.toggleRadius,
    p: t.roles.togglePadding,
    gap: t.roles.toggleGap,
    flexShrink: 0,
  },

  /**
   * One composed literal rather than two objects the call site merges — an
   * `sx={[a, b]}` array of style-file objects is what trips TS2769.
   * The active "varies" half reads brand accent; the active "fixed" half ink.
   */
  toggleItem: (active: boolean, isVary: boolean) => ({
    [PHONE]: {
      flex: 1,
      justifyContent: "center",
      height: t.mobile.toggleItemHeight,
    },
    height: t.roles.toggleItemHeight,
    px: t.roles.toggleItemPaddingX,
    borderRadius: t.roles.toggleItemRadius,
    display: "flex",
    alignItems: "center",
    fontSize: t.roles.toggleFontSize,
    fontWeight: t.roles.toggleFontWeight,
    fontFamily: "inherit",
    border: "none",
    cursor: "pointer",
    whiteSpace: "nowrap",
    backgroundColor: active ? p.white : "transparent",
    color: active ? (isVary ? p.accent : p.ink) : p.muted,
    boxShadow: active ? t.roles.toggleActiveShadow : "none",
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "2px",
    },
  }),

  /** "VERIFY WITH" / "PHONE" — the small caps label before a right-side control. */
  inlineLabel: {
    fontSize: t.roles.deliverLabelFontSize,
    fontWeight: 700,
    letterSpacing: t.roles.deliverLabelLetterSpacing,
    textTransform: "uppercase",
    color: p.muted,
    whiteSpace: "nowrap",
  },

  /** Label + control, held together and pushed to the row's right edge. */
  inlineGroup: {
    display: "flex",
    alignItems: "center",
    gap: t.roles.deliverGap,
    ml: "auto",
    flexShrink: 0,
    [PHONE]: { ml: 0, width: "100%" },
  },

  /*
    The column list is the only notice a varying role gets, so it wraps rather
    than truncating — an ellipsis here would hide the very column the sender
    has just made required.
  */
  /**
   * Shares the "Deliver via" row instead of claiming one of its own — the note
   * is a consequence of the choice made on that row, and a full-width banner
   * for it cost more height than it was worth.
   */
  deliverNotice: {
    display: "flex",
    alignItems: "center",
    gap: t.roles.noticeGap,
    p: t.roles.noticePadding,
    borderRadius: t.roles.noticeRadius,
    backgroundColor: p.warnBg,
    border: `1px solid ${p.warnBorder}`,
    fontSize: t.roles.noticeFontSize,
    color: p.warnText,
    ml: "auto",
    minWidth: 0,
    [PHONE]: { ml: 0, width: "100%" },
  },

  /**
   * Row two of a role card: who fills it, and how they verify. Carries no top
   * border — it belongs to the identity above it, not to the delivery strip
   * below, which is where the card's first divider falls.
   */
  fillRow: {
    display: "flex",
    alignItems: "center",
    gap: t.roles.fillGap,
    p: t.roles.fillPadding,
    flexWrap: "wrap",
    [PHONE]: { p: "0 14px 14px", gap: "9px" },
  },

  /**
   * The design's own 38px field, not the 44px app-wide box — this card packs a
   * name, an email and a security control onto one line, and the taller shared
   * chrome breaks that line. Same raw-input approach step 4's grid already
   * takes, so the two recipient surfaces stay consistent with each other.
   */
  roleInput: (hasError: boolean) => ({
    width: "100%",
    height: t.roles.inputHeight,
    border: `${t.roles.inputBorderWidth} solid ${
      hasError ? t.check.invalidBorder : p.borderStrong
    }`,
    borderRadius: t.roles.inputRadius,
    backgroundColor: p.white,
    fontFamily: "inherit",
    fontSize: t.roles.inputFontSize,
    fontWeight: 600,
    color: p.ink,
    px: t.roles.inputPaddingX,
    boxSizing: "border-box",
    "&::placeholder": { color: p.muted, fontWeight: 500 },
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "1px",
    },
  }),

  /** The shared phone control, sized so it ends where the fields above do. */
  phoneWrap: {
    width: t.roles.phoneWidth,
    flexShrink: 0,
    "& .MuiFormHelperText-root": { display: "none" },
    /* The control fills the box it was given, so its right edge is the row's. */
    "& .MuiFormControl-root, & .MuiTextField-root": { width: "100%" },
    [PHONE]: { width: "100%" },
  },

  /** One field plus the message under it, so an error cannot widen the row. */
  roleField: (grow: number, minWidth: number = 0) => ({
    flex: grow || "0 0 auto",
    minWidth,
    display: "flex",
    flexDirection: "column",
  }),

  roleFieldMessage: {
    fontSize: t.check.invalidMessageFontSize,
    color: t.check.invalidChipText,
    mt: t.check.invalidMessageMarginTop,
    lineHeight: 1.4,
  },

  fixedInputs: {
    flex: 1,
    display: "flex",
    alignItems: "flex-start",
    gap: t.roles.inputGap,
    minWidth: 0,
    flexWrap: "wrap",
    [PHONE]: { flexDirection: "column", width: "100%", gap: "9px" },
  },

  security: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    height: t.roles.securityHeight,
    px: t.roles.securityPaddingX,
    border: `1px solid ${p.borderStrong}`,
    borderRadius: t.roles.securityRadius,
    backgroundColor: p.white,
    fontSize: t.roles.securityFontSize,
    fontWeight: 600,
    color: p.ink,
    whiteSpace: "nowrap",
    flexShrink: 0,
    boxSizing: "border-box",
  },

  /** "Deliver via" sits on its own line under the role, as the design has it. */
  deliverRow: {
    display: "flex",
    alignItems: "center",
    gap: t.roles.deliverGap,
    p: t.roles.deliverPadding,
    borderTop: `1px solid ${p.borderSoft}`,
    flexWrap: "wrap",
    [PHONE]: { p: `12px 14px`, gap: "8px" },
  },

  deliverLabel: {
    fontSize: t.roles.deliverLabelFontSize,
    fontWeight: 700,
    letterSpacing: t.roles.deliverLabelLetterSpacing,
    textTransform: "uppercase",
    color: p.muted,
    whiteSpace: "nowrap",
  },

  channel: (checked: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: t.roles.channelGap,
    height: t.roles.channelHeight,
    px: t.roles.channelPaddingX,
    borderRadius: t.roles.channelRadius,
    border: `1.5px solid ${checked ? p.accent : p.borderStrong}`,
    backgroundColor: p.white,
    color: p.ink,
    fontFamily: "inherit",
    fontSize: t.roles.channelFontSize,
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "2px",
    },
  }),

  channelBox: (checked: boolean) => ({
    width: t.roles.channelBoxSize,
    height: t.roles.channelBoxSize,
    borderRadius: t.roles.channelBoxRadius,
    border: `1.5px solid ${checked ? p.accent : p.borderStrong}`,
    backgroundColor: checked ? p.accent : p.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),

  orderNote: {
    display: "flex",
    alignItems: "center",
    gap: t.roles.orderNoteGap,
    p: t.roles.orderNotePadding,
    borderTop: `1px solid ${p.borderSoft}`,
    fontSize: t.roles.orderNoteFontSize,
    color: p.muted,
  },

  warning: {
    display: "flex",
    gap: t.roles.warningGap,
    mt: t.roles.warningMarginTop,
    p: t.roles.warningPadding,
    borderRadius: t.roles.warningRadius,
    backgroundColor: p.warnBg,
    border: `1px solid ${p.warnBorder}`,
    fontSize: t.roles.warningFontSize,
    color: p.warnText,
    lineHeight: t.roles.warningLineHeight,
  },

  formError: {
    mt: "14px",
    fontSize: t.roles.warningFontSize,
    color: p.dangerText,
  },
};

/** Step 4 — recipients: three ways in, all landing on the same create call. */
export const recipientsStyles = {
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: t.recipients.headerGap,
    mb: t.recipients.headerMarginBottom,
    flexWrap: "wrap",
  },

  routes: {
    display: "flex",
    gap: t.recipients.routesGap,
    mb: t.recipients.routesMarginBottom,
    flexWrap: "wrap",
  },

  route: (active: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: t.recipients.routeGap,
    height: t.recipients.routeHeight,
    px: t.recipients.routePaddingX,
    borderRadius: t.recipients.routeRadius,
    border: `${t.recipients.routeBorderWidth} solid ${
      active ? p.accent : p.borderLight
    }`,
    backgroundColor: active ? p.accentTint : p.white,
    color: active ? p.accent : p.body,
    fontSize: t.recipients.routeFontSize,
    fontWeight: 700,
    fontFamily: "inherit",
    cursor: "pointer",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "2px",
    },
  }),

  dropzone: {
    border: `1.5px dashed ${p.borderMain}`,
    borderRadius: t.recipients.dropzoneRadius,
    p: t.recipients.dropzonePadding,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: t.recipients.dropzoneGap,
    cursor: "pointer",
    textAlign: "left",
    width: "100%",
    backgroundColor: p.white,
    fontFamily: "inherit",
    boxSizing: "border-box",
    "&:hover": { borderColor: p.accent },
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "2px",
    },
  },

  dropzoneTitle: {
    fontSize: t.recipients.dropzoneTitleFontSize,
    fontWeight: t.recipients.dropzoneTitleFontWeight,
    color: p.ink,
  },

  dropzoneHint: {
    fontSize: t.recipients.dropzoneHintFontSize,
    color: p.muted,
  },

  note: {
    display: "flex",
    alignItems: "center",
    gap: t.recipients.noteGap,
    mt: t.recipients.noteMarginTop,
    p: t.recipients.notePadding,
    borderRadius: t.recipients.noteRadius,
    backgroundColor: p.surface,
    fontSize: t.recipients.noteFontSize,
    color: p.body,
    lineHeight: 1.6,
  },
};

/** Step 4, typed rows — one row per recipient, a cell per varying role. */
export const typedRowsStyles = {
  scroller: {
    overflowX: "auto",
    border: `1px solid ${p.borderLight}`,
    borderRadius: "12px",
    backgroundColor: p.white,
  },

  /**
   * Only the rows scroll: the column headings and "Add a recipient" stay put,
   * so a sender 400 rows down still knows which column they are in and can
   * still add one.
   */
  rowsArea: {
    maxHeight: t.typedRows.rowsMaxHeight,
    overflowY: "auto",
    overscrollBehavior: "contain",
  },

  grid: (columns: string) => ({
    display: "grid",
    gridTemplateColumns: columns,
    gap: t.typedRows.rowGap,
    alignItems: "start",
    px: "16px",
    minWidth: 520,
  }),

  header: {
    fontSize: t.typedRows.headerFontSize,
    fontWeight: 700,
    letterSpacing: t.typedRows.headerLetterSpacing,
    textTransform: "uppercase",
    color: p.muted,
    py: t.typedRows.headerPaddingY,
    borderBottom: `1px solid ${p.borderSoft}`,
  },

  rowNumber: {
    width: t.typedRows.numberWidth,
    height: t.typedRows.inputHeight,
    display: "flex",
    alignItems: "center",
    fontSize: t.typedRows.numberFontSize,
    fontWeight: 700,
    color: p.muted,
    fontVariantNumeric: "tabular-nums",
  },

  /** A row the sender chose to skip stays readable but visibly out of play. */
  row: (isSkipped: boolean) => ({
    px: "16px",
    py: t.typedRows.rowPaddingY,
    opacity: isSkipped ? 0.55 : 1,
  }),

  /** Wraps an input plus the inline message under it, so the grid cell owns both. */
  cell: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },

  inlineMessage: {
    fontSize: t.check.invalidMessageFontSize,
    color: t.check.invalidChipText,
    mt: t.check.invalidMessageMarginTop,
    lineHeight: 1.4,
  },

  /**
   * The shared contact-number control, sized to the grid's own field so a
   * phone cell lines up with the name and email beside it. Its helper text is
   * hidden because the row renders the message itself, in the same place every
   * other column puts one.
   */
  phoneCell: {
    width: "100%",
    "& .MuiFormControl-root": { width: "100%" },
    "& .MuiFormHelperText-root": { display: "none" },
    "& .MuiOutlinedInput-root": {
      height: t.typedRows.inputHeight,
      borderRadius: t.typedRows.inputRadius,
      fontSize: t.typedRows.inputFontSize,
    },
  },

  input: (hasError: boolean) => ({
    width: "100%",
    height: t.typedRows.inputHeight,
    border: `1.5px solid ${hasError ? t.check.invalidBorder : p.borderStrong}`,
    borderRadius: t.typedRows.inputRadius,
    backgroundColor: p.white,
    fontFamily: "inherit",
    fontSize: t.typedRows.inputFontSize,
    fontWeight: 500,
    color: p.ink,
    px: "12px",
    boxSizing: "border-box",
    "&::placeholder": { color: p.muted },
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "1px",
    },
  }),

  remove: {
    width: t.typedRows.removeSize,
    height: t.typedRows.inputHeight,
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    backgroundColor: "transparent",
    color: p.muted,
    cursor: "pointer",
    "&:hover": { color: p.dangerText, backgroundColor: p.dangerBg },
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "1px",
    },
  },

  addRow: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    flexWrap: "wrap",
    px: "16px",
    py: "12px",
  },

  pasteHint: {
    fontSize: t.typedRows.headerFontSize,
    color: p.muted,
  },

  required: {
    color: p.accent,
    ml: "3px",
  },

  requiredNote: {
    display: "block",
    mt: "3px",
    fontSize: t.typedRows.headerFontSize,
    fontWeight: 600,
    letterSpacing: "normal",
    textTransform: "none",
    color: p.warnText,
  },

  add: {
    display: "flex",
    alignItems: "center",
    gap: t.typedRows.addGap,
    height: t.typedRows.addHeight,
    px: "14px",
    border: `1.5px dashed ${p.borderMain}`,
    borderRadius: t.typedRows.addRadius,
    backgroundColor: "transparent",
    color: p.body,
    fontFamily: "inherit",
    fontSize: t.typedRows.addFontSize,
    fontWeight: 700,
    cursor: "pointer",
    "&:hover": { borderColor: p.accent, color: p.accent },
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "2px",
    },
  },

  footer: {
    display: "flex",
    alignItems: "center",
    gap: t.typedRows.footerGap,
    pt: t.typedRows.footerPaddingTop,
    flexWrap: "wrap",
  },

  count: {
    flex: 1,
    minWidth: 0,
    fontSize: t.typedRows.countFontSize,
    color: p.muted,
  },
};

/**
 * Step 4 — the check panel that opens under the recipient table.
 *
 * The amber band carries the verdict; the flagged rows sit on white beneath
 * it, inside the same border. The footer is deliberately *outside* the panel,
 * on the page itself, because continuing is the step's action rather than the
 * panel's.
 */
export const checkStyles = {
  panel: {
    mt: t.check.panelMarginTop,
    borderRadius: t.check.panelRadius,
    border: `1px solid ${t.check.panelBorder}`,
    backgroundColor: t.check.rowBg,
    overflow: "hidden",
    boxSizing: "border-box",
  },

  head: {
    display: "flex",
    alignItems: "flex-start",
    gap: t.check.headGap,
    p: t.check.headPadding,
    backgroundColor: t.check.panelBg,
    flexWrap: "wrap",
  },

  headIcon: {
    display: "flex",
    flexShrink: 0,
    mt: "1px",
    color: t.check.panelIcon,
  },

  banner: {
    display: "block",
    fontSize: t.check.bannerFontSize,
    fontWeight: t.check.bannerFontWeight,
    color: p.ink,
  },

  sub: {
    display: "block",
    fontSize: t.check.subFontSize,
    color: p.body,
    mt: t.check.subMarginTop,
  },

  chips: {
    display: "flex",
    alignItems: "center",
    gap: t.check.chipsGap,
    ml: "auto",
    flexWrap: "wrap",
  },

  chip: (tone: "ready" | "invalid" | "incomplete" | "blank") => ({
    display: "inline-flex",
    alignItems: "baseline",
    gap: t.check.chipCountGap,
    p: t.check.chipPadding,
    borderRadius: t.check.chipRadius,
    fontSize: t.check.chipFontSize,
    fontWeight: t.check.chipFontWeight,
    whiteSpace: "nowrap",
    backgroundColor:
      tone === "ready"
        ? t.check.readyChipBg
        : tone === "invalid"
          ? t.check.invalidChipBg
          : tone === "incomplete"
            ? t.check.incompleteChipBg
            : t.check.blankChipBg,
    border: `${t.check.chipBorderWidth} solid ${
      tone === "ready"
        ? t.check.readyChipBorder
        : tone === "invalid"
          ? t.check.invalidChipBorder
          : tone === "incomplete"
            ? t.check.incompleteChipBorder
            : t.check.blankChipBorder
    }`,
    color:
      tone === "ready"
        ? t.check.readyChipText
        : tone === "invalid"
          ? t.check.invalidChipText
          : tone === "incomplete"
            ? t.check.incompleteChipText
            : t.check.blankChipText,
  }),

  chipCount: {
    fontWeight: 800,
    fontVariantNumeric: "tabular-nums",
  },

  worklist: {
    display: "flex",
    flexDirection: "column",
  },

  row: {
    display: "flex",
    alignItems: "center",
    gap: t.check.rowGap,
    p: t.check.rowPadding,
    borderTop: `1px solid ${t.check.panelBorder}`,
    backgroundColor: t.check.rowBg,
    flexWrap: "wrap",
  },

  rowLabel: {
    width: t.check.rowLabelWidth,
    flexShrink: 0,
    fontSize: t.check.rowLabelFontSize,
    fontWeight: 700,
    color: p.label,
    fontVariantNumeric: "tabular-nums",
  },

  rowName: {
    flex: `0 0 ${t.check.rowNameWidth}px`,
    fontSize: t.check.rowNameFontSize,
    fontWeight: 700,
    color: p.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  rowReason: {
    flex: 1,
    minWidth: 0,
    fontSize: t.check.rowReasonFontSize,
    color: t.check.invalidChipText,
  },

  rowActions: {
    display: "flex",
    alignItems: "center",
    gap: t.check.rowActionGap,
    flexShrink: 0,
  },

  rowAction: (isSkip: boolean) => ({
    border: "none",
    backgroundColor: "transparent",
    p: 0,
    fontFamily: "inherit",
    fontSize: t.check.rowActionFontSize,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
    color: isSkip ? p.label : p.accent,
    "&:hover": { textDecoration: "underline" },
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "2px",
      borderRadius: "4px",
    },
  }),

  /** On the page, not in the panel: continuing is the step's action. */
  footer: {
    display: "flex",
    alignItems: "center",
    gap: t.check.footerGap,
    mt: t.check.footerMarginTop,
    flexWrap: "wrap",
  },

  footerText: {
    flex: 1,
    minWidth: 0,
    fontSize: t.check.footerFontSize,
    color: p.body,
  },
};

/** Step 4 — the validation worklist, shown in place once the file is read. */
export const reportStyles = {
  panel: {
    width: "100%",
    maxWidth: t.report.width,
    border: `1px solid ${p.borderLight}`,
    borderRadius: t.report.radius,
    backgroundColor: p.white,
    overflow: "hidden",
    boxSizing: "border-box",
  },

  header: {
    display: "flex",
    alignItems: "center",
    gap: t.report.headerGap,
    p: t.report.headerPadding,
    borderBottom: `1px solid ${p.borderLight}`,
  },

  headerIcon: (clean: boolean) => ({
    width: t.report.headerIconSize,
    height: t.report.headerIconSize,
    borderRadius: t.report.headerIconRadius,
    backgroundColor: clean ? p.successBg : p.warnBg,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),

  headerTitle: {
    display: "block",
    fontSize: t.report.headerTitleFontSize,
    fontWeight: 800,
    color: p.ink,
  },

  headerSub: {
    display: "block",
    fontSize: t.report.headerSubFontSize,
    color: p.muted,
    mt: "2px",
  },

  stats: {
    display: "flex",
    gap: t.report.statsGap,
    p: t.report.statsPadding,
    flexWrap: "wrap",
  },

  stat: (tone: "success" | "danger" | "warning") => ({
    flex: "1 1 120px",
    border: `1px solid ${
      tone === "success"
        ? p.successBorder
        : tone === "danger"
          ? p.dangerBorder
          : p.warnBorder
    }`,
    backgroundColor:
      tone === "success"
        ? p.successBg
        : tone === "danger"
          ? p.dangerBg
          : p.warnBg,
    borderRadius: t.report.statRadius,
    p: t.report.statPadding,
    boxSizing: "border-box",
  }),

  statNumber: (tone: "success" | "danger" | "warning") => ({
    display: "block",
    fontSize: t.report.statNumberFontSize,
    fontWeight: 800,
    color:
      tone === "success"
        ? p.successText
        : tone === "danger"
          ? p.dangerText
          : p.warnText,
  }),

  statLabel: (tone: "success" | "danger" | "warning") => ({
    display: "block",
    fontSize: t.report.statLabelFontSize,
    fontWeight: 700,
    mt: "2px",
    color:
      tone === "success"
        ? p.successText
        : tone === "danger"
          ? p.dangerText
          : p.warnText,
  }),

  sectionLabel: {
    p: t.report.sectionLabelPadding,
    fontSize: t.report.sectionLabelFontSize,
    fontWeight: 800,
    letterSpacing: ".08em",
    textTransform: "uppercase",
    color: p.muted,
  },

  row: {
    display: "flex",
    alignItems: "center",
    gap: t.report.rowGap,
    p: t.report.rowPadding,
    borderTop: `1px solid ${p.borderSoft}`,
  },

  rowLine: {
    width: t.report.rowLineWidth,
    fontSize: t.report.rowLineFontSize,
    fontWeight: 700,
    color: p.muted,
    flexShrink: 0,
  },

  rowValue: {
    display: "block",
    fontSize: t.report.rowValueFontSize,
    fontWeight: 700,
    color: p.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  rowMessage: (severity: "error" | "warning") => ({
    display: "block",
    fontSize: t.report.rowMessageFontSize,
    mt: "2px",
    color: severity === "error" ? p.dangerText : p.warnText,
  }),

  footer: {
    display: "flex",
    alignItems: "center",
    gap: t.report.rowGap,
    p: t.report.footerPadding,
    borderTop: `1px solid ${p.borderLight}`,
    backgroundColor: p.surfaceHover,
    flexWrap: "wrap",
  },

  footerText: {
    flex: 1,
    minWidth: 0,
    fontSize: t.report.footerFontSize,
    color: p.body,
  },
};

/** Step 5's document card, collapsible settings, and the email preview. */
export const reviewCardStyles = {
  /**
   * The step opens on this: what is going out, to how many people, how it
   * reaches them and when they are nudged — the four facts a sender checks
   * before committing, rather than a restatement of the settings below.
   */
  factCard: {
    border: `1px solid ${p.borderLight}`,
    borderRadius: t.review.factCardRadius,
    backgroundColor: p.white,
    p: t.review.factCardPadding,
    boxSizing: "border-box",
  },

  factRow: {
    display: "grid",
    /* `minmax(0, …)` so a long document name cannot widen the grid. */
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: t.review.factColumnGap,
    [PHONE]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
  },

  factLabel: {
    display: "block",
    fontSize: t.review.factLabelFontSize,
    fontWeight: 700,
    letterSpacing: t.review.factLabelLetterSpacing,
    textTransform: "uppercase",
    color: p.muted,
    whiteSpace: "nowrap",
  },

  factValue: {
    display: "block",
    mt: t.review.factValueMarginTop,
    fontSize: t.review.factValueFontSize,
    fontWeight: 700,
    color: p.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  factDivider: {
    height: "1px",
    backgroundColor: p.borderLight,
    my: t.review.factDividerMargin,
  },

  factRoles: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: t.review.factRolesGap,
  },

  roleChip: (color: string) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "7px",
    p: t.reviewCards.chipPadding,
    borderRadius: t.reviewCards.chipRadius,
    backgroundColor: p.surface,
    border: `1px solid ${p.borderLight}`,
    fontSize: t.reviewCards.chipFontSize,
    fontWeight: 700,
    color: p.body,
    "&::before": {
      content: "''",
      width: t.reviewCards.chipDotSize,
      height: t.reviewCards.chipDotSize,
      borderRadius: "50%",
      backgroundColor: color,
    },
  }),

  preview: {
    mt: "18px",
    border: `1px solid ${p.borderLight}`,
    borderRadius: t.reviewCards.previewRadius,
    overflow: "hidden",
    backgroundColor: p.white,
  },

  previewHead: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    p: t.reviewCards.previewHeaderPadding,
    backgroundColor: p.ctaBg,
    color: p.white,
    fontSize: t.reviewCards.previewHeaderFontSize,
    fontWeight: 700,
  },

  previewRowPill: {
    ml: "auto",
    p: t.reviewCards.previewRowPillPadding,
    borderRadius: "999px",
    backgroundColor: p.white,
    color: p.ctaBg,
    fontSize: t.reviewCards.previewRowPillFontSize,
    fontWeight: 800,
    whiteSpace: "nowrap",
  },

  previewBody: {
    p: t.reviewCards.previewBodyPadding,
    display: "flex",
    flexDirection: "column",
    gap: t.reviewCards.previewMetaGap,
  },

  previewMeta: {
    fontSize: t.reviewCards.previewMetaFontSize,
    color: p.body,
    overflowWrap: "anywhere",
  },

  previewDivider: {
    height: "1px",
    backgroundColor: p.borderLight,
    my: "10px",
  },

  previewText: {
    fontSize: t.reviewCards.previewTextFontSize,
    color: p.ink,
    lineHeight: 1.6,
    overflowWrap: "anywhere",
  },

  previewButton: {
    alignSelf: "flex-start",
    height: t.reviewCards.previewButtonHeight,
    px: "18px",
    borderRadius: t.reviewCards.previewButtonRadius,
    backgroundColor: p.ctaBg,
    color: p.white,
    display: "flex",
    alignItems: "center",
    fontSize: "13px",
    fontWeight: 700,
    mt: "6px",
  },

  previewNote: {
    display: "flex",
    gap: "8px",
    fontSize: t.reviewCards.previewNoteFontSize,
    color: p.muted,
    lineHeight: 1.55,
  },

  previewFooter: {
    display: "flex",
    alignItems: "center",
    gap: "9px",
    p: t.reviewCards.previewFooterPadding,
    borderTop: `1px solid ${p.borderLight}`,
    fontSize: "10.5px",
    color: p.muted,
  },

  previewBrand: {
    p: "2px 7px",
    borderRadius: "4px",
    backgroundColor: p.ctaBg,
    color: p.white,
    fontSize: "10px",
    fontWeight: 800,
  },

  previewCaption: {
    mt: t.reviewCards.previewCaptionMarginTop,
    fontSize: t.reviewCards.previewCaptionFontSize,
    color: p.muted,
    lineHeight: 1.55,
  },

  /** Closing note: security and delivery live on step 3, not here. */
  crossRef: {
    display: "flex",
    gap: "11px",
    mt: t.reviewCards.sectionGap,
    p: "14px 16px",
    borderRadius: "10px",
    backgroundColor: p.surface,
    fontSize: "12px",
    color: p.body,
    lineHeight: 1.6,
  },
};

/** Step 5 — review, with the send panel down the right. */
/** The batch page — progress, failures, and what can be done about them. */
export const batchStyles = {
  card: {
    border: `1px solid ${p.borderLight}`,
    borderRadius: t.batch.cardRadius,
    backgroundColor: p.white,
    overflow: "hidden",
    width: "100%",
    boxSizing: "border-box",
  },

  header: {
    display: "flex",
    alignItems: "center",
    gap: t.batch.headerGap,
    p: t.batch.headerPadding,
    borderBottom: `1px solid ${p.borderLight}`,
    flexWrap: "wrap",
  },

  title: {
    display: "block",
    fontSize: t.batch.titleFontSize,
    fontWeight: t.batch.titleFontWeight,
    color: p.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  sub: {
    display: "block",
    fontSize: t.batch.subFontSize,
    color: p.muted,
    mt: t.batch.subMarginTop,
  },

  /**
   * One pill per lifecycle state. `CompletedWithErrors` deliberately reads
   * danger rather than success — it is the state that means rows are worth
   * retrying, and it has to be distinguishable from `Cancelled` at a glance.
   */
  statusPill: (tone: "neutral" | "progress" | "success" | "danger") => ({
    display: "flex",
    alignItems: "center",
    gap: t.batch.pillGap,
    height: t.batch.pillHeight,
    px: t.batch.pillPaddingX,
    borderRadius: t.batch.pillRadius,
    backgroundColor:
      tone === "success"
        ? p.successBg
        : tone === "danger"
          ? p.dangerBg
          : tone === "progress"
            ? p.warnBg
            : p.pill,
    border: `1px solid ${
      tone === "success"
        ? p.successBorder
        : tone === "danger"
          ? p.dangerBorder
          : tone === "progress"
            ? p.warnBorder
            : p.borderLight
    }`,
    color:
      tone === "success"
        ? p.successText
        : tone === "danger"
          ? p.dangerText
          : tone === "progress"
            ? p.warnText
            : p.label,
    fontSize: t.batch.pillFontSize,
    fontWeight: t.batch.pillFontWeight,
    whiteSpace: "nowrap",
    flexShrink: 0,
    boxSizing: "border-box",
  }),

  statusDot: (color: string) => ({
    width: t.batch.pillDotSize,
    height: t.batch.pillDotSize,
    borderRadius: "50%",
    backgroundColor: color,
    flexShrink: 0,
  }),

  body: {
    p: t.batch.bodyPadding,
  },

  bar: {
    display: "flex",
    height: t.batch.barHeight,
    borderRadius: t.batch.barRadius,
    overflow: "hidden",
    backgroundColor: p.surface,
    mb: t.batch.barMarginBottom,
  },

  barSegment: (width: string, color: string) => ({
    width,
    backgroundColor: color,
  }),

  legend: {
    display: "flex",
    flexWrap: "wrap",
    gap: t.batch.legendGap,
  },

  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: t.batch.legendItemGap,
  },

  legendDot: (color: string) => ({
    width: t.batch.legendDotSize,
    height: t.batch.legendDotSize,
    borderRadius: t.batch.legendDotRadius,
    backgroundColor: color,
    flexShrink: 0,
  }),

  legendNumber: {
    fontSize: t.batch.legendNumberFontSize,
    fontWeight: 700,
    color: p.ink,
    fontVariantNumeric: "tabular-nums",
  },

  legendLabel: {
    fontSize: t.batch.legendLabelFontSize,
    color: p.muted,
  },

  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    p: t.batch.sectionPadding,
    borderTop: `1px solid ${p.borderLight}`,
  },

  sectionLabel: {
    fontSize: t.batch.sectionLabelFontSize,
    fontWeight: 800,
    letterSpacing: t.batch.sectionLabelLetterSpacing,
    textTransform: "uppercase",
    color: p.muted,
  },

  failedRow: {
    display: "flex",
    alignItems: "center",
    gap: t.batch.rowGap,
    p: t.batch.rowPadding,
    borderTop: `1px solid ${p.borderSoft}`,
  },

  failedAvatar: {
    width: t.batch.rowAvatarSize,
    height: t.batch.rowAvatarSize,
    borderRadius: "50%",
    backgroundColor: p.dangerBg,
    color: p.dangerText,
    fontSize: t.batch.rowAvatarFontSize,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  failedName: {
    display: "block",
    fontSize: t.batch.rowNameFontSize,
    fontWeight: 700,
    color: p.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  failedContact: {
    display: "block",
    fontSize: t.batch.rowContactFontSize,
    color: p.muted,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  reason: {
    fontSize: t.batch.reasonFontSize,
    fontWeight: 700,
    color: p.dangerText,
    backgroundColor: p.dangerBg,
    borderRadius: "999px",
    p: t.batch.reasonPadding,
    maxWidth: 260,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    flexShrink: 0,
  },

  footer: {
    display: "flex",
    alignItems: "center",
    gap: t.batch.footerGap,
    p: t.batch.footerPadding,
    borderTop: `1px solid ${p.borderLight}`,
    backgroundColor: p.surfaceHover,
    flexWrap: "wrap",
  },

  footerNote: {
    flex: 1,
    minWidth: 0,
    fontSize: t.batch.footerNoteFontSize,
    color: p.muted,
  },

  actionButton: {
    height: t.batch.footerButtonHeight,
    borderRadius: t.batch.footerButtonRadius,
    px: t.batch.footerButtonPaddingX,
    fontSize: t.batch.footerButtonFontSize,
    whiteSpace: "nowrap",
  },

  filters: {
    display: "flex",
    alignItems: "center",
    gap: t.batch.filtersGap,
    p: t.batch.filtersPadding,
    borderBottom: `1px solid ${p.borderSoft}`,
    flexWrap: "wrap",
  },

  filter: (active: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: "7px",
    height: t.batch.filterHeight,
    px: t.batch.filterPaddingX,
    borderRadius: t.batch.filterRadius,
    border: `${t.batch.filterBorderWidth} solid ${
      active ? p.accent : p.borderLight
    }`,
    backgroundColor: active ? p.accentTint : p.white,
    color: active ? p.accent : p.body,
    fontSize: t.batch.filterFontSize,
    fontWeight: 700,
    fontFamily: "inherit",
    cursor: "pointer",
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "2px",
    },
  }),

  filterCount: {
    fontSize: t.batch.filterCountFontSize,
    fontWeight: 800,
    opacity: 0.7,
    fontVariantNumeric: "tabular-nums",
  },

  tableHeader: {
    [PHONE]: { display: "none" },
    display: "grid",
    gridTemplateColumns: t.batch.tableColumns,
    gap: t.batch.tableRowGap,
    alignItems: "center",
    p: t.batch.tableHeaderPadding,
    borderBottom: `1px solid ${p.borderSoft}`,
    fontSize: t.batch.tableHeaderFontSize,
    fontWeight: 700,
    letterSpacing: t.batch.tableHeaderLetterSpacing,
    textTransform: "uppercase",
    color: p.muted,
  },

  tableRow: {
    display: "grid",
    gridTemplateColumns: t.batch.tableColumns,
    gap: t.batch.tableRowGap,
    /* A wrapped reason makes the row tall; the rest aligns to its first line. */
    alignItems: "flex-start",
    p: t.batch.tableRowPadding,
    borderBottom: `1px solid ${p.borderSoft}`,
    [PHONE]: {
      gridTemplateColumns: "1fr",
      gap: "6px",
      p: `14px ${t.mobile.gutter}`,
    },
  },

  tableAvatar: (tone: "danger" | "neutral") => ({
    width: t.batch.tableAvatarSize,
    height: t.batch.tableAvatarSize,
    borderRadius: "50%",
    backgroundColor: tone === "danger" ? p.dangerBg : p.pill,
    color: tone === "danger" ? p.dangerText : p.label,
    fontSize: t.batch.tableAvatarFontSize,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),

  tableName: {
    display: "block",
    fontSize: t.batch.tableNameFontSize,
    fontWeight: 700,
    color: p.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  /**
   * The server's own reason for a failed row. It wraps rather than
   * ellipsising, because on this page the reason *is* the content — a
   * truncated "Exception submitting sign request. Reason :: Sign r…" tells
   * the sender nothing they can act on.
   */
  tableMeta: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 3,
    overflow: "hidden",
    fontSize: t.batch.tableMetaFontSize,
    color: p.muted,
    mt: "2px",
    lineHeight: 1.45,
    overflowWrap: "anywhere",
  },

  tableNote: {
    display: "flex",
    alignItems: "center",
    gap: "11px",
    p: t.batch.notePadding,
    backgroundColor: p.surfaceHover,
    fontSize: t.batch.footerNoteFontSize,
    color: p.body,
    lineHeight: 1.6,
  },
};

/** Batches grouped in the Sent list. */
export const batchListStyles = {
  group: {
    borderBottom: `1px solid ${p.borderSoft}`,
  },

  row: {
    [PHONE]: { flexWrap: "wrap", p: `13px ${t.mobile.gutter}` },
    display: "flex",
    alignItems: "center",
    gap: t.batchList.rowGap,
    p: t.batchList.rowPadding,
    width: "100%",
    border: "none",
    backgroundColor: "transparent",
    fontFamily: "inherit",
    textAlign: "left",
    cursor: "pointer",
    boxSizing: "border-box",
    "&:hover": { backgroundColor: p.surfaceHover },
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "-2px",
    },
  },

  chevron: (expanded: boolean) => ({
    display: "flex",
    flexShrink: 0,
    color: p.muted,
    transform: expanded ? "rotate(90deg)" : "none",
    transition: "transform .15s ease",
  }),

  badge: {
    display: "flex",
    alignItems: "center",
    height: t.batchList.badgeHeight,
    px: t.batchList.badgePaddingX,
    borderRadius: t.batchList.badgeRadius,
    backgroundColor: p.accentTint,
    border: `1px solid ${p.accentBorder}`,
    fontSize: t.batchList.badgeFontSize,
    fontWeight: 800,
    color: p.accent,
    letterSpacing: t.batchList.badgeLetterSpacing,
    flexShrink: 0,
    boxSizing: "border-box",
  },

  name: {
    display: "block",
    fontSize: t.batchList.nameFontSize,
    fontWeight: 700,
    color: p.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  meta: {
    display: "block",
    fontSize: t.batchList.metaFontSize,
    color: p.muted,
    mt: t.batchList.metaMarginTop,
  },

  miniBar: {
    [PHONE]: { width: "100%", order: 3 },
    display: "flex",
    width: t.batchList.miniBarWidth,
    height: t.batchList.miniBarHeight,
    borderRadius: t.batchList.miniBarRadius,
    overflow: "hidden",
    backgroundColor: p.surface,
    flexShrink: 0,
  },

  status: (tone: "success" | "danger" | "progress" | "neutral") => ({
    width: t.batchList.statusWidth,
    textAlign: "right",
    fontSize: t.batchList.statusFontSize,
    fontWeight: 700,
    flexShrink: 0,
    color:
      tone === "success"
        ? p.successText
        : tone === "danger"
          ? p.dangerText
          : tone === "progress"
            ? p.warnText
            : p.muted,
  }),

  children: {
    p: t.batchList.childrenPadding,
    display: "flex",
    flexDirection: "column",
  },

  childRow: {
    display: "flex",
    alignItems: "center",
    gap: t.batchList.childGap,
    p: t.batchList.childRowPadding,
    borderTop: `1px solid ${p.borderSoft}`,
  },

  childAvatar: (tone: "success" | "danger" | "neutral") => ({
    width: t.batchList.childAvatarSize,
    height: t.batchList.childAvatarSize,
    borderRadius: "50%",
    backgroundColor:
      tone === "success"
        ? p.successBg
        : tone === "danger"
          ? p.dangerBg
          : p.pill,
    color:
      tone === "success"
        ? p.successText
        : tone === "danger"
          ? p.dangerText
          : p.label,
    fontSize: t.batchList.childAvatarFontSize,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),

  childName: {
    flex: 1,
    minWidth: 0,
    fontSize: t.batchList.childNameFontSize,
    fontWeight: 600,
    color: p.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  childWhen: {
    fontSize: t.batchList.childWhenFontSize,
    color: p.muted,
    flexShrink: 0,
  },

  childStatus: (tone: "success" | "danger" | "neutral") => ({
    width: t.batchList.statusWidth,
    textAlign: "right",
    fontSize: t.batchList.childStatusFontSize,
    fontWeight: 700,
    flexShrink: 0,
    color:
      tone === "success"
        ? p.successText
        : tone === "danger"
          ? p.dangerText
          : p.muted,
  }),

  seeAll: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: t.batchList.seeAllHeight,
    fontSize: t.batchList.seeAllFontSize,
    fontWeight: 700,
    color: p.accent,
    cursor: "pointer",
    border: "none",
    backgroundColor: "transparent",
    fontFamily: "inherit",
    width: "100%",
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "-2px",
    },
  },
};

/** Phone chrome: collapsed stepper in the header, primary action in a footer. */
export const bulkMobileStyles = {
  header: {
    position: "sticky",
    top: 0,
    zIndex: 20,
    backgroundColor: p.white,
    borderBottom: `1px solid ${p.borderLight}`,
  },

  headerRow: {
    height: 52,
    display: "flex",
    alignItems: "center",
    gap: "11px",
    px: t.mobile.gutter,
  },

  backButton: {
    width: 34,
    height: 34,
    ml: "-6px",
    borderRadius: "9px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    backgroundColor: "transparent",
    color: p.ink,
    cursor: "pointer",
    flexShrink: 0,
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "2px",
    },
  },

  headerTitle: {
    display: "block",
    fontSize: "14.5px",
    fontWeight: 800,
    color: p.ink,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  stepperBlock: {
    px: t.mobile.gutter,
    pb: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "7px",
  },

  stepperRow: {
    display: "flex",
    alignItems: "baseline",
    gap: "7px",
  },

  stepCount: {
    fontSize: t.mobile.stepLabelFontSize,
    fontWeight: 800,
    letterSpacing: ".1em",
    textTransform: "uppercase",
    color: p.accent,
    whiteSpace: "nowrap",
  },

  stepName: {
    fontSize: t.mobile.stepNameFontSize,
    fontWeight: 700,
    color: p.ink,
    whiteSpace: "nowrap",
  },

  stepNext: {
    ml: "auto",
    fontSize: t.mobile.stepLabelFontSize,
    fontWeight: 600,
    color: p.muted,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  bars: {
    display: "flex",
    gap: t.mobile.stepBarGap,
  },

  bar: (done: boolean) => ({
    flex: 1,
    height: t.mobile.stepBarHeight,
    borderRadius: "999px",
    backgroundColor: done ? p.accent : p.borderLight,
  }),

  footer: {
    position: "sticky",
    bottom: 0,
    zIndex: 20,
    backgroundColor: p.white,
    borderTop: `1px solid ${p.borderLight}`,
    p: t.mobile.footerPadding,
    display: "flex",
    gap: t.mobile.footerGap,
  },

  footerSecondary: {
    height: t.mobile.footerButtonHeight,
    px: "18px",
    borderRadius: t.mobile.footerButtonRadius,
    border: `1.5px solid ${p.borderStrong}`,
    backgroundColor: p.white,
    color: p.body,
    fontFamily: "inherit",
    fontSize: "13.5px",
    fontWeight: 700,
    whiteSpace: "nowrap",
    flexShrink: 0,
    cursor: "pointer",
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "2px",
    },
  },

  footerPrimary: (disabled: boolean) => ({
    flex: 1,
    height: t.mobile.footerButtonHeight,
    borderRadius: t.mobile.footerButtonRadius,
    border: "none",
    backgroundColor: disabled ? p.borderMain : p.ctaBg,
    color: p.white,
    fontFamily: "inherit",
    fontSize: t.mobile.footerButtonFontSize,
    fontWeight: 700,
    cursor: disabled ? "default" : "pointer",
    "&:focus-visible": {
      outline: `2px solid ${p.accent}`,
      outlineOffset: "2px",
    },
  }),

  /** Step bodies: the desktop 40px gutter is 14px here, and rows stack. */
  page: {
    px: t.mobile.gutter,
    pt: t.mobile.bodyPaddingTop,
    pb: t.mobile.bodyPaddingBottom,
  },

  title: {
    fontSize: t.mobile.titleFontSize,
    fontWeight: 800,
    letterSpacing: "-.02em",
    color: p.ink,
  },

  sub: {
    fontSize: t.mobile.subFontSize,
    color: p.muted,
    lineHeight: 1.6,
  },
};

/** Shared helper: the muted "meta" line under a title. */
export const metaLine = (theme: Theme) => ({
  fontSize: t.templatePicker.metaFontSize,
  color: p.muted,
  [theme.breakpoints.down("md")]: { fontSize: "11px" },
});

export default bulkShellStyles;
